/**
 * CandidateRow — one entry of the candidate inbox. Demo page: /email (inbox row: title, badges, preview, meta).
 */
import { Clock, ExternalLink, ShieldCheck } from 'lucide-react';
import { Badge, Button } from '../../../components/ui/index.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { safeHref } from '../../../lib/safe-url.js';
import type { CatalogCandidateListItem } from './catalog-types.js';
import {
  buildCatalogSourceEvidenceHref,
  candidateLabel,
  describeCatalogCandidateIntegrity,
  statusVariant,
} from './catalog-helpers.js';

export function CandidateRow({
  candidate,
  selected,
  onOpen,
}: {
  candidate: CatalogCandidateListItem;
  selected: boolean;
  onOpen: () => void;
}) {
  const label = candidateLabel(candidate);
  const sourceUrl = buildCatalogSourceEvidenceHref(null, candidate.source);
  const integrityState = describeCatalogCandidateIntegrity(candidate);

  return (
    <div className={selected ? 'bg-accent/5 ring-1 ring-accent/30 ring-inset' : 'hover:bg-surface-alt'}>
      <Button
        variant="ghost"
        aria-pressed={selected}
        onClick={onOpen}
        className="h-auto w-full flex-col items-start justify-start gap-0 rounded-none p-4 text-left font-normal whitespace-normal"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-heading">{label}</span>
          <Badge variant={statusVariant(candidate.status)}>{sentenceCase(candidate.status)}</Badge>
          <Badge variant="neutral">{sentenceCase(candidate.targetType)}</Badge>
          {integrityState.hasBlockingChecks && <Badge variant="danger">Integrity blocked</Badge>}
          {!integrityState.hasBlockingChecks && integrityState.isSourceOnly && (
            <Badge variant="warning">Source only</Badge>
          )}
        </span>
        <span className="mt-2 line-clamp-2 text-sm text-muted">{candidate.diffSummary ?? 'No summary supplied.'}</span>
        {integrityState.summary && (
          <span className="mt-2 line-clamp-2 text-xs text-warning">{integrityState.summary}</span>
        )}
        <span className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted">
          <span className="inline-flex items-center gap-1">
            <ShieldCheck size={13} aria-hidden="true" /> {sentenceCase(candidate.proposedAction)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock size={13} aria-hidden="true" />{' '}
            {candidate.queuedAt ? new Date(candidate.queuedAt).toLocaleString() : 'Queued'}
          </span>
          {(candidate.discoveredByDaemonName || candidate.discoveredByEntityId) && (
            <span>{candidate.discoveredByDaemonName ?? 'Discovery daemon'}</span>
          )}
        </span>
      </Button>
      {sourceUrl && (
        <div className="px-4 pb-3 text-xs">
          <a
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
            href={safeHref(sourceUrl) ?? undefined}
            target="_blank"
            rel="noreferrer"
          >
            Source <ExternalLink size={13} aria-hidden="true" />
          </a>
        </div>
      )}
    </div>
  );
}
